<template>
  <div class="flex-grow-1 table-analytics">
    <div ref="" class="mb-3">
      <a-form-item class="mb-0">
        <a-checkbox
          v-model="isEnableContentEngine"
          @change="(checkEvent) => onChangeEnableContentEngine(checkEvent.target.checked)"
        >
          Enable Content &amp; Integrity Analytics
          <a-tooltip placement="top">
            <template slot="title">
              <span class="fs-10">
                <span>
                  The webpage is analyzed and base-lined for its
                  contents including the HTML title, number of links,
                  images, scripts, style sheets, etc.
                </span>
                <br>
                <span>
                  Subsequent checks will be compared with the baseline
                  and the results fed into an advanced decision making
                  algorithm to determine if the webpage has been
                  modified.
                </span>
                <br>
                <span>
                  A threshold can be set to allow for some deviation
                  (plus/minus) from the baseline value. The default
                  threshold value is 0.
                </span>
              </span>
            </template>
            <a-icon
              class="explain"
              type="info-circle"
              theme="filled"
            />
          </a-tooltip>
        </a-checkbox>
      </a-form-item>
    </div>
    <a-table
      rowKey="index"
      :rowSelection="
        isEnableContentEngine
          ? {
            selectedRowKeys: selectedRowKeys,
            onChange: onSelectChange,
          }
          :null"
      :columns="AnalyticsColumns"
      :dataSource="recommendedValues"
      :pagination="recommendedValuesPagin"
    >
      <template slot="description" slot-scope="description">
        <span>
          {{ description }}
          <smart-image-hash-conf v-if="description === 'Images'" :conf="smartImageHashConf" @onChangeSmartImageHashConf="onChangeSmartImageHashConf" />
          <a-tooltip
            v-if="description === 'Full Page Signature'"
            placement="top"
          >
            <template slot="title">
              <span class="fs-10">
                BETA: Detect changes by hashing the contents of the entire page.
                Only use this feature on a static HTML site.
              </span>
            </template>
            <a-icon class="explain" type="info-circle" theme="filled" />
          </a-tooltip>
        </span>
      </template>
      <template slot="lastVerifiedValue" slot-scope="lastVerifiedValue">
        <span>{{ lastVerifiedValue }}</span>
      </template>
      <template slot="baselineValue" slot-scope="baselineValue, record">
        <span v-if="baselineValueNotEditable.includes(record.description)">
          {{ baselineValue }}
        </span>
        <a-input
          v-if="!baselineValueNotEditable.includes(record.description)"
          type="text"
          :defaultValue="baselineValue"
          :disabled="!selectedRowKeys.includes(record.index)"
          @change="(e) => onChangeBaselineValue(e.target.value, record.index)"
        />
      </template>
      <template slot="threshold" slot-scope="threshold, record">
        <a-input
          v-show="record.typeHasThreshold"
          type="text"
          :defaultValue="threshold"
          :disabled="!selectedRowKeys.includes(record.index)"
          @change="(e) => onChangeThreshold(e.target.value, record.index)"
        />
      </template>
      <template slot="detail" slot-scope="detail, record">
        <a-dropdown
          v-if="record.typeHasDetail"
          v-model="record.visibleDropBox"
          :placement="'bottomRight'"
        >
          <a-menu slot="overlay" class="detail-dropdown">
            <a-menu-item
              v-for="(item, index) in record.detail"
              :key="item.id"
              class="detail-item-readonly"
            >
              <a-tooltip placement="left">
                <template slot="title">
                  <span class="fs-10">
                    {{ item.name }}
                  </span>
                </template>
                <a-checkbox
                  :disabled="!selectedRowKeys.includes(record.index)"
                  :defaultChecked="item.selected"
                  @change="
                    (e) =>
                      onClickCheckDetail(e.target.checked, record.index, index)
                  "
                >
                  {{ `${index + 1}.${item.name}` }}
                </a-checkbox>
                <a-button v-if="item.html" size="small" @click="onViewElementHtmlClicked(item)">
                  View HTML
                </a-button>
              </a-tooltip>
            </a-menu-item>
          </a-menu>
          <a-button size="large" @click="(e) => e.preventDefault()">
            View Detail <a-icon type="down" />
          </a-button>
        </a-dropdown>
      </template>
    </a-table>
    <a-modal
      v-model="htmlModal.isVisible"
      :title="htmlModal.title"
      :footer="null"
    >
      <pre>{{ htmlModal.html }}</pre>
    </a-modal>
  </div>
</template>

<script>
import WebpageService from "../services/webpage.service";
import { AnalyticsColumns } from "../helper.js";
import SmartImageHashConf from "./SmartImageHashConf";

export default {
  name: "ContentAnalytics",
  components: {SmartImageHashConf},
  data() {
    return {
      AnalyticsColumns,
      recommendedValues: [],
      baselineValueNotEditable: ["Full Page Signature", "HTML Title"],
      selectedRowKeys: [],
      isEnableContentEngine: false,
      htmlModal: {
          isVisible: false,
          title: "",
          html: "",
      },
      smartImageHashConf: {}
    };
  },
  computed: {
    recommendedValuesPagin() {
      return {
        current: 1,
        pageSize: this.recommendedValues.length,
        total: this.recommendedValues.length,
      }
    }
  },
  mounted() {
    this.initData();
  },
  methods: {
    /**
     * ---> initData > decodeBase64ToUtf8 > recommendedValues (displayed in a-table)
     * Decodes a base64 string to UTF-8 (utf8mb4 compatible) if prefixed with [B64:].
     * Returns the original string if no prefix or if decoding fails.
     */
    decodeBase64ToUtf8(str) {
      if (!str || typeof str !== 'string') {
        return str;
      }
      
      const BASE64_PREFIX = '[B64:]';
      
      // Only decode if explicitly marked with [B64:] prefix
      if (!str.startsWith(BASE64_PREFIX)) {
        return str;
      }
      
      try {
        // Extract the base64 content after the prefix
        const base64Content = str.slice(BASE64_PREFIX.length);
        // Decode base64 to binary string
        const binaryString = atob(base64Content);
        // Convert binary string to Uint8Array for proper UTF-8 decoding
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        // Decode as UTF-8 (supports full utf8mb4 including 4-byte chars like emojis)
        return new TextDecoder('utf-8').decode(bytes);
      } catch (e) {
        // If decoding fails, return original string
        return str;
      }
    },
    async initData() {
      const {
        threshold,
        custom_element_threshold,
        last_verified,
        current_baseline: baseline,
        smart_image_hash_conf,
        policies,
      } = await WebpageService.get(+this.$route.params.id);
      this.smartImageHashConf = smart_image_hash_conf;
      if ( this.smartImageHashConf == null) {
        this.smartImageHashConf = {imgHashEnable: 0, imgHashSensitivity: 10 }
      }
      if (policies.length != 0) {
        this.smartImageHashConf['policy_setting'] = policies[0]['smart_image_hash_configurations'];
      }
      this.recommendedValues = [
        {
          enable: !!threshold && !!threshold.enable_title,
          index: 1,
          description: "HTML Title",
          // Decode base64 to UTF-8 (utf8mb4) for title values if encoded with [B64:] prefix
          lastVerifiedValue: this.decodeBase64ToUtf8(last_verified && last_verified.title),
          baselineValue: this.decodeBase64ToUtf8(baseline && baseline.title),
          threshold: -1,
          detail: [],
          typeHasDetail: false,
          typeHasThreshold: false,
          visibleDropBox: false,
        },
        {
          enable: !!threshold && !!threshold.enable_href,
          index: 2,
          description: "Href Links",
          lastVerifiedValue: last_verified && last_verified.count_href,
          baselineValue: baseline && baseline.count_href,
          threshold: threshold && threshold.threshold_href,
          detail: baseline && baseline.baseline_hrefs,
          typeHasDetail: true,
          typeHasThreshold: true,
          visibleDropBox: false,
        },
        {
          enable: !!threshold && !!threshold.enable_img,
          index: 3,
          description: "Images",
          lastVerifiedValue: last_verified && last_verified.count_image,
          baselineValue: baseline && baseline.count_image,
          threshold: threshold && threshold.threshold_img,
          detail: baseline && baseline.baseline_images,
          typeHasDetail: true,
          typeHasThreshold: true,
          visibleDropBox: false,
          enable_sih: this.smartImageHashConf.imgHashEnable,
          sih_threshold: this.smartImageHashConf.imgHashSensitivity,
        },
        {
          enable: !!threshold && !!threshold.enable_script,
          index: 4,
          description: "Javascripts",
          lastVerifiedValue: last_verified && last_verified.count_script,
          baselineValue: baseline && baseline.count_script,
          threshold: threshold && threshold.threshold_script,
          detail: baseline && baseline.baseline_scripts,
          typeHasDetail: true,
          typeHasThreshold: true,
          visibleDropBox: false,
        },
        {
          enable: !!threshold && !!threshold.enable_link,
          index: 5,
          description: "Cascading Styles Sheets",
          lastVerifiedValue: last_verified && last_verified.count_link,
          baselineValue: baseline && baseline.count_link,
          threshold: threshold && threshold.threshold_link,
          detail: baseline && baseline.baseline_stylesheets,
          typeHasDetail: true,
          typeHasThreshold: true,
          visibleDropBox: false,
        },
        {
          enable: !!threshold && !!threshold.enable_iframe,
          index: 6,
          description: "Iframes",
          lastVerifiedValue: last_verified && last_verified.count_iframe,
          baselineValue: baseline && baseline.count_iframe,
          threshold: threshold && threshold.threshold_iframe,
          detail: baseline && baseline.baseline_iframes,
          typeHasDetail: true,
          typeHasThreshold: true,
          visibleDropBox: false,
        },
        {
          enable: !!threshold && !!threshold.enable_form,
          index: 7,
          description: "Forms",
          lastVerifiedValue: last_verified && last_verified.count_form,
          baselineValue: baseline && baseline.count_form,
          threshold: threshold && threshold.threshold_form,
          detail: baseline && baseline.baseline_forms,
          typeHasDetail: true,
          typeHasThreshold: true,
          visibleDropBox: false,
        },
        {
          enable: !!threshold && !!threshold.enable_div,
          index:  8,
          description: "Divisions",
          lastVerifiedValue: last_verified && last_verified.count_div,
          baselineValue: baseline && baseline.count_div,
          threshold: threshold && threshold.threshold_div,
          detail: [],
          typeHasDetail: false,
          typeHasThreshold: true,
          visibleDropBox: false,
        },
        {
          enable: !!threshold && !!threshold.enable_line_num,
          index: 9,
          description: "# of HTML Lines",
          lastVerifiedValue: last_verified && last_verified.line_num,
          baselineValue: baseline && baseline.line_num,
          threshold: threshold && threshold.threshold_line_num,
          detail: [],
          typeHasDetail: false,
          typeHasThreshold: true,
          visibleDropBox: false,
        },
        {
          enable: !!threshold && !!threshold.enable_hash,
          index: 10,
          description: "Full Page Signature",
          lastVerifiedValue:
            last_verified &&
            last_verified.hash &&
            last_verified.hash.substring(10),
          baselineValue:
            baseline && baseline.hash && baseline.hash.substring(10),
          threshold: -1,
          detail: [],
          typeHasDetail: false,
          typeHasThreshold: false,
          visibleDropBox: false,
        },
      ];

      //console.log(custom_element_threshold);

      for (const customElementCount of baseline.baseline_custom_element_counts) {
        const ceThresh = custom_element_threshold.find((element) => element.def_id === customElementCount.def_id);
        this.recommendedValues.push({
          enable: ceThresh && ceThresh.enable,
          index: this.recommendedValues.length + 1,
          description: customElementCount.custom_element_definition ? "Custom Element: " + customElementCount.custom_element_definition.element_name : "Custom Element: [Deleted]",
          lastVerifiedValue: 'Not Available',
          baselineValue: customElementCount.count,
          threshold: ceThresh && ceThresh.threshold,
          detail: baseline.baseline_custom_elements.filter((element) => element.def_id === customElementCount.def_id),
          typeHasDetail: true,
          typeHasThreshold: true,
          visibleDropBox: false,
          isCustomElement: true,
          customElementCountId: customElementCount.id,
        })
      }

      this.isEnableContentEngine = !!threshold && !!threshold.enable_content_engine;

      this.selectedRowKeys = this.isEnableContentEngine
        ? this.recommendedValues
            .filter((item) => item.enable)
            .map((item) => item.index)
        : [];
      this.$emit("recommendedValuesChange", this.recommendedValues);
    },
    onSelectChange(selectedRowKeys) {
      this.selectedRowKeys = selectedRowKeys;
      const newRecommendedValues = [...this.recommendedValues].map((item) => ({
        ...item,
        enable: selectedRowKeys.includes(item.index),
      }));
      this.$emit("recommendedValuesChange", newRecommendedValues);
    },
    onChangeThreshold(value, recordIndex) {
      const newData = [...this.recommendedValues];
      const target = newData.find((item) => item.index === recordIndex);
      if (target) {
        target.threshold = value;
        this.recommendedValues = newData;
        this.$emit("recommendedValuesChange", newData);
      }
    },
    onChangeBaselineValue(value, recordIndex) {
      const newData = [...this.recommendedValues];
      const target = newData.find((item) => item.index === recordIndex);
      if (target) {
        target.baselineValue = value;
        this.recommendedValues = newData;
        this.$emit("recommendedValuesChange", newData);
      }
    },
    onClickCheckDetail(isChecked, recordIndex, detailIndex) {
      const newData = [...this.recommendedValues];
      const target = newData.find((item) => item.index === recordIndex);
      if (target && target.detail && target.detail.length) {
        target.detail[detailIndex].selected = isChecked;
        this.recommendedValues = newData;
        this.$emit("recommendedValuesChange", newData);
      }
    },
    onChangeEnableContentEngine(isChecked) {
      this.selectedRowKeys = isChecked
        ? this.recommendedValues.map((item) => item.index)
        : [];
      const newRecommendedValues = [...this.recommendedValues].map((item) => ({
        ...item,
        enable: isChecked,
      }));
      this.$emit("recommendedValuesChange", newRecommendedValues);
    },
    onViewElementHtmlClicked(item) {
        this.htmlModal.title = item.name;
        this.htmlModal.html = item.html;
        this.htmlModal.isVisible = true;
    },
    onChangeSmartImageHashConf(changed_conf) {
      const imageConf = this.recommendedValues.find(item => item.description === 'Images');
      imageConf["enable_sih"] = changed_conf["enable_sih"];
      imageConf["sih_threshold"] = changed_conf["sih_threshold"];
      this.$emit("recommendedValuesChange", this.recommendedValues);
    }
  },
};
</script>
<style lang="scss" scoped>
.table-analytics {
  .ant-table-thead {
    font-weight: 500;
    tr th {
      background: #fff;
    }
  }
}
.detail-item-readonly {
  cursor: default;
  .ant-checkbox-wrapper {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    width: 250px;
  }
}
.detail-dropdown {
  overflow: auto;
  height: auto;
  max-height: 400px;
}
.prevent-event {
  pointer-events: none;
}
</style>
